import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Spinner } from 'react-bootstrap';
import { apiPost } from '../../../utils/api';
import { AnalyzerPanelProps } from './DiscourseMarkersPanel';

/**
 * Mode d'anàlisi: classificació de textos per nivell CEFR (A1–C2).
 * Càrrega mandrosa: quan s'obre la pestanya, envia el text pla al proxy del
 * backend (/v1/nlp/classify → classificador LLaMA CEFR) i mostra el nivell
 * estimat, la confiança i la distribució de probabilitats. El model talla a
 * ~2048 tokens; si el text es trunca, ho avisem.
 */
interface ClassifyResult {
    label: string;
    label_id: number;
    confidence: number;
    probabilities: Record<string, number>;
    n_tokens: number;
    truncated: boolean;
    warning: string | null;
    latency_ms: number;
}

const CEFR_LEVELS = ['A1', 'A2', 'B1', 'B2', 'C1', 'C2'];

export default function ClassifierPanel(props: AnalyzerPanelProps) {
    const { t } = useTranslation();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(false);
    const [result, setResult] = useState<ClassifyResult | null>(null);

    const text = (props.plainText || '').trim();

    useEffect(() => {
        if (!text) {
            setResult(null);
            setError(false);
            return;
        }
        let cancelled = false;
        setLoading(true);
        setError(false);
        setResult(null);
        apiPost<ClassifyResult>('/v1/nlp/classify', { text })
            .then(res => {
                if (cancelled) return;
                if (res.data && res.data.label) {
                    setResult(res.data);
                } else {
                    setError(true);
                }
            })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [text]);

    if (!text) {
        return (
            <div className="classifier-panel">
                <p className="classifier-empty">{ t("There is no text to analyze.") }</p>
            </div>
        );
    }

    if (loading) {
        return (
            <div className="classifier-panel classifier-loading">
                <Spinner animation="border" size="sm" />
                <span>{ t("Classifying the text…") }</span>
            </div>
        );
    }

    if (error || !result) {
        return (
            <div className="classifier-panel">
                <Alert variant="danger">{ t("We couldn't classify this text. Please try again.") }</Alert>
            </div>
        );
    }

    const confidencePct = Math.round(result.confidence * 100);

    return (
        <div className="classifier-panel">
            <div className="classifier-result">
                <div className={ 'classifier-badge level-' + result.label }>{ result.label }</div>
                <div className="classifier-summary">
                    <div className="classifier-heading">{ t("Estimated CEFR level") }</div>
                    <div className="classifier-confidence">{ t("Confidence") }: <strong>{ confidencePct }%</strong></div>
                </div>
            </div>

            { result.truncated &&
                <Alert variant="warning" className="classifier-truncated">
                    { t("The text exceeded the model limit (~2048 tokens) and was truncated: the level reflects only the beginning of the text.") }
                </Alert>
            }

            <div className="classifier-distribution">
                <div className="classifier-distribution-title">{ t("Level distribution") }</div>
                { CEFR_LEVELS.map(level => {
                    const pct = Math.round(((result.probabilities?.[level]) || 0) * 100);
                    return (
                        <div key={ level } className={ 'classifier-bar-row' + (level === result.label ? ' predicted' : '') }>
                            <span className="classifier-bar-label">{ level }</span>
                            <span className="classifier-bar-track">
                                <span className="classifier-bar-fill" style={{ width: pct + '%' }} />
                            </span>
                            <span className="classifier-bar-pct">{ pct }%</span>
                        </div>
                    );
                }) }
            </div>
        </div>
    );
}
